import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Only enabled in production environment
 */
export function initializeSentry() {
  const environment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || "development";

  // Only initialize Sentry in production
  if (environment !== "production") {
    console.info("Sentry disabled in non-production environment");
    return;
  }

  // Only initialize Sentry if DSN is configured
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn("Sentry DSN not configured. Error tracking will be disabled.");
    return;
  }

  const release = import.meta.env.VITE_SENTRY_RELEASE || `taxassist-frontend@${import.meta.env.VITE_APP_VERSION || "unknown"}`;

  Sentry.init({
    dsn,
    environment,
    release,

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration({
        // Track React Router navigation
        enableInp: true,
      }),
      // Replay sessions for debugging
      Sentry.replayIntegration({
        // Only capture replays for errors
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: environment === "production" ? 0.1 : 1.0, // 10% in prod, 100% in dev

    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions
    replaysOnErrorSampleRate: 1.0, // Always capture replays on error

    // PII and Logging
    sendDefaultPii: false, // Don't send PII by default for privacy
    beforeSend(event) {
      // Filter out sensitive data
      if (event.request?.cookies) {
        delete event.request.cookies;
      }
      if (event.request?.headers) {
        // Remove sensitive headers
        const headers = event.request.headers;
        delete headers?.authorization;
        delete headers?.cookie;
      }
      return event;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      // Random plugins/extensions
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      // Network errors that are expected
      "NetworkError",
      "Failed to fetch",
    ],

    // Sample rate for errors
    sampleRate: 1.0, // Capture all errors
  });

  // Set user context if available (will be set after authentication)
  Sentry.setTag("app", "taxassist-frontend");

  console.info(`Sentry initialized for environment: ${environment}`);
}

/**
 * Set user context in Sentry
 */
export function setSentryUser(user: { userId: string; email?: string; role?: string; firmIds?: string[] }) {
  Sentry.setUser({
    id: user.userId,
    email: user.email,
    role: user.role,
  });

  // Set firm IDs as context
  if (user.firmIds && user.firmIds.length > 0) {
    Sentry.setContext("firms", {
      firmIds: user.firmIds,
      firmCount: user.firmIds.length,
    });
  }
}

/**
 * Clear user context in Sentry (on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Manually capture an exception
 */
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext("additional", context);
    }
    scope.captureException(error);
  });
}

/**
 * Manually capture a message
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = "info", context?: Record<string, any>) {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext("additional", context);
    }
    scope.captureMessage(message, level);
  });
}
